import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';

import { getError } from '@/utils/hookFormUtils';

import type { RefObject } from 'react';

import AddressInput, { AddressInputRef } from './AddressInput';
import { destinationAddressForm } from './constants';
import DirectionForm from './DirectionForm';
import InputRef from './InputRef';

interface DestinationAddressSectionProps {
  control: any;
  register: any;
  errors: any;
  setValue: any;
  watch: any;
  geoSuggestRef?: RefObject<AddressInputRef | null>;
  userCountry?: any;
  showDirectionForm: { inOrigin: boolean; inDestination: boolean };
  showZipCodeInput: { inOrigin: boolean; inDestination: boolean };
  findAddressComponent: any;
  handleZipCodeNotFound: (site: 'inOrigin' | 'inDestination', notFound: boolean) => void;
  handleShowDirectionForm: (site: 'inOrigin' | 'inDestination') => void;
  onOpenSavedAddresses?: () => void;
}

const DestinationAddressSection = (props: DestinationAddressSectionProps) => {
  const {
    control,
    register,
    errors,
    setValue,
    watch,
    geoSuggestRef,
    userCountry,
    showDirectionForm,
    showZipCodeInput,
    findAddressComponent,
    handleZipCodeNotFound,
    handleShowDirectionForm,
    onOpenSavedAddresses,
  } = props;

  return (
    <div className="space-y-2">
      <div className="flex flex-row justify-between items-center w-full mt-2 mb-4 px-1 flex-wrap gap-2">
        <FormControlLabel
          control={
            <Checkbox
              checked={showDirectionForm.inDestination}
              onChange={() => handleShowDirectionForm('inDestination')}
              name={'destination.showForm'}
              color="primary"
              size={'small'}
              className="!p-1"
            />
          }
          label={'¿No encuentras tu dirección?'}
          sx={{ '&& span': { fontSize: '0.875rem', color: 'text.secondary' } }}
        />

        {onOpenSavedAddresses && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: '0.25rem',
            }}
            onClick={onOpenSavedAddresses}
            width={'fit-content'}
          >
            <BookmarksOutlinedIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Destinatarios guardados
            </Typography>
          </Box>
        )}
      </div>

      {!showDirectionForm.inDestination && (
        <AddressInput
          control={control}
          name="destination"
          findAddressComponent={findAddressComponent}
          setValue={setValue}
          ref={geoSuggestRef}
          country={userCountry}
          onZipCodeNotFound={(notFound) => handleZipCodeNotFound('inDestination', notFound)}
        />
      )}
      {!showDirectionForm.inDestination && showZipCodeInput.inDestination && (
        <Grid size={{ xxs: 12 }} sx={{ mt: 2 }}>
          <TextField
            {...register('destination.zipCode')}
            value={watch('destination.zipCode') || ''}
            size="small"
            variant="outlined"
            fullWidth
            label="Código postal *"
            error={!!getError(errors, 'destination.zipCode')}
            helperText={getError(errors, 'destination.zipCode')?.message}
            slotProps={{
              inputLabel: {
                shrink: Boolean(watch('destination.zipCode')) || undefined,
              },
              htmlInput: {
                autoComplete: 'off',
                role: 'presentation',
                'data-form-type': 'other',
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
              },
            }}
          />
        </Grid>
      )}

      {showDirectionForm.inDestination && (
        <DirectionForm
          register={register}
          errors={errors}
          directionData={destinationAddressForm}
          watch={watch}
        />
      )}
      <InputRef errors={errors} register={register} name="destination.reference" watch={watch} />
    </div>
  );
};

export default DestinationAddressSection;
